import type { WritableAtom } from 'jotai';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- Jotai RESET is a `unique symbol`; atom generics need `typeof RESET`, which requires a value binding
import { atomWithStorage, createJSONStorage, RESET } from 'jotai/utils';

/**
 * localStorage key for the user's preferred site (cloudId). Versioned so we
 * can change the storage shape later without colliding with old values.
 */
export const SELECTED_SITE_STORAGE_KEY = 'twg-microsite.selectedSite.v1';

/**
 * Per-user persisted site choice (cloudId), backed by `localStorage` via
 * Jotai's official `atomWithStorage`. Behaviour:
 *
 *   - Read on first subscription (and synchronously at module init thanks to
 *     `getOnInit: true`) so `LoggedInGate`'s one-shot `useHydrateAtoms`
 *     can pick up the saved value before the first render commits.
 *   - Written automatically every time the atom is set, so call sites only
 *     need `useSetAtom(selectedSiteCloudIdAtom)` and never touch
 *     `localStorage` directly.
 *   - Synced across tabs via the browser's native `storage` event listener
 *     that `atomWithStorage` registers for us.
 *   - Falls back to in-memory storage when `localStorage` is unavailable
 *     (SSR, sandboxed iframes, Safari private mode quota errors), so the
 *     atom is always safe to read/write without try/catch at the call site.
 *
 * Consumers MUST still validate the returned cloudId against the current
 * available-sites list — a saved tenant may have been removed/deactivated
 * since the user last picked it, in which case we want to fall back to
 * the default selection criteria rather than pin them to a broken state.
 */
export const selectedSiteCloudIdAtom: WritableAtom<
	string | null,
	[string | typeof RESET | ((prev: string | null) => string | typeof RESET | null) | null],
	void
> = atomWithStorage<string | null>(
	SELECTED_SITE_STORAGE_KEY,
	null,
	createJSONStorage<string | null>(() => localStorage),
	{ getOnInit: true },
);
